import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { SettingEntity } from '@/types/api';

export const getSettings = async (
  orgId: string,
  projectId: string | undefined,
): Promise<SettingEntity[]> => {
  if (projectId) {
    const { data } = await api.get<unknown, ApiResponse<SettingEntity[]>>(
      `/orgs/${orgId}/projects/${projectId}/settings`,
    );
    return data;
  }
  const { data } = await api.get<unknown, ApiResponse<SettingEntity[]>>(`/orgs/${orgId}/settings`);
  return data;
};

export const getSettingsQueryKey = (
  orgId: string | null | undefined,
  projectId: string | null | undefined = '',
) => {
  const queryKey = ['settings'];
  if (typeof orgId === 'string') {
    queryKey.push('org', orgId);
    if (typeof projectId === 'string') {
      queryKey.push('project', projectId);
    }
  }
  return queryKey;
};

export const getSettingsQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null = '',
) => {
  return queryOptions({
    queryKey: getSettingsQueryKey(orgId, projectId),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getSettings(orgId!, projectId!),
    enabled: !!orgId && typeof projectId === 'string',
  });
};

interface UseSettingsOptions {
  projectId?: string;
  queryConfig?: QueryConfig<typeof getSettingsQueryOptions>;
}

export const useSettings = ({ projectId, queryConfig = {} }: UseSettingsOptions = {}) => {
  const { orgId } = useScopeIds('org');
  const { projectId: projectIdFromScope } = useScopeIds();

  return useQuery({
    ...getSettingsQueryOptions(orgId, projectId ?? projectIdFromScope),
    ...queryConfig,
  });
};
